<template>
	<VerticalLayout size="middle">
		<Skeleton v-if="showTopButtons" :paragraph="{ rows: 1 }" :title="false" :loading="loading">
			<HorizontalLayout v-if="filterOptions.length > 0" size="middle" :direction="buttonsDirection">
				<TypographyText v-if="maxCssrMountpointsCount === 1">
					<Space v-if="filterOptions.length > 0">
						{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.fullMountpointLabel') }}
						<TypographyText strong>{{ filterOptions[0].label }}</TypographyText>
					</Space>
					<span v-else>  </span>
				</TypographyText>
				<Select
					v-else
					class="ui-select"
					v-model:value="filterSelectedId"
					:options="filterOptions"
					:disabled="disabled"
					option-label-prop="label"
				/>
				<HorizontalLayout v-if="maxCssrMountpointsCount !== 1">
					<Button class="width-100" type="primary" :disabled="disabled" @click="onCreateCssrMountpointClick">
						<PlusCircleOutlined />{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.addButtonLabel') }}
					</Button>
					<PopconfirmButton
						:title="$t('settings.vrsSettingsForm.cssrMessagesFilters.deleteConfirmation')"
						:ok-text="$t('settings.vrsSettingsForm.cssrMessagesFilters.deleteButtonLabel')"
						:cancel-text="$t('settings.vrsSettingsForm.cssrMessagesFilters.cancelButtonLabel')"
						:disabled="disabled || filterOptions.length <= 1"
						:onConfirm="onDeleteCssrMountpointConfirm"
						placement="topRight"
						buttonClass="width-100"
					>
						{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.deleteButtonLabel') }}
					</PopconfirmButton>
				</HorizontalLayout>
			</HorizontalLayout>
			<VerticalLayout v-else>
				<TypographyParagraph>
					{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.description') }}
				</TypographyParagraph>
				<Button type="primary" :disabled="disabled" @click="onCreateCssrMountpointClick">
					<PlusCircleOutlined />{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.addButtonLabel') }}
				</Button>
			</VerticalLayout>
		</Skeleton>
		<Skeleton :paragraph="{ rows: 2 }" :title="false" :loading="loading">
			<VerticalLayout>
				<Descriptions
					:column="1"
					:colon="false"
					bordered
					size="small"
					:labelStyle="{ backgroundColor: 'white' }"
					:contentStyle="{ backgroundColor: 'white' }"
				>
					<DescriptionsItem :label="$t('settings.sub-networks.vrsSettingsForm.finalNameCssr')">
						{{ settings.finalNameCssr }}
					</DescriptionsItem>
				</Descriptions>
				<div class="mmi-mode-container pd-vertical">
					<TypographyText strong>{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.mmiModeLabel') }}</TypographyText>
					<LabeledSwitch
						v-model="mmiModeEnabled"
						leftLabel="mode 0"
						rightLabel="mode 1"
						:loading="isMmiModeSubmitting"
						:disabled="disabled || loading || cssrFilters.id === 0 || settings.deleted || isMmiModeSubmitting"
						:allowWordWrap="false"
						@change="onMmiModeChange"
					/>
					<QuestionCircleFilled
						v-if="isMmiDescExpanded"
						class="mmi-hint-icon mmi-hint-icon--active"
						@click="toggleMmiDesc"
					/>
					<QuestionCircleOutlined
						v-else
						class="mmi-hint-icon"
						@click="toggleMmiDesc"
					/>
				</div>
				<div
					class="mmi-description-collapse"
					:class="{ 'mmi-description-collapse--expanded': isMmiDescExpanded }"
				>
					<div class="mmi-description-collapse__inner">
						<TypographyParagraph class="mmi-description-text">
							{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.mmiModeDescription.intro') }}
						</TypographyParagraph>
						<TypographyParagraph class="mmi-description-text">
							{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.mmiModeDescription.mode1') }}
						</TypographyParagraph>
						<TypographyParagraph class="mmi-description-text">
							{{ $t('settings.vrsSettingsForm.cssrMessagesFilters.mmiModeDescription.mode0') }}
						</TypographyParagraph>
					</div>
				</div>
			</VerticalLayout>
		</Skeleton>
		<CssrMessagesFiltersForm
			:loading="loading"
			:status="status"
			:settings="settings"
			:cssrFilters="cssrFilters"
			:fetchSettings="fetchSettings"
			:disabled="disabled"
		/>
	</VerticalLayout>
</template>

<script>
import { ref, computed, toRef, watch } from 'vue';
import { Space, Select, Button, Skeleton, TypographyParagraph, TypographyText, Descriptions, DescriptionsItem } from 'ant-design-vue';
import { PlusCircleOutlined, QuestionCircleOutlined, QuestionCircleFilled } from '@ant-design/icons-vue';

import { useSmallScreen } from '@/utils/useSmallScreen';

import Api from '@/api/Api';
import { showErrorMessage } from '@/components/common/errorMessage';

import VerticalLayout from '@/components/common/VerticalLayout';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import HorizontalLayout from '@/components/common/HorizontalLayout.vue';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';

import CssrMessagesFiltersForm from '@/components/settings/cssrFilters/CssrMessagesFiltersForm';

export default {
	components: {
		Space, Select, Button, PlusCircleOutlined, QuestionCircleOutlined, QuestionCircleFilled, Skeleton, TypographyParagraph, TypographyText, Descriptions, DescriptionsItem,
		CssrMessagesFiltersForm,
		LabeledSwitch,
		VerticalLayout, PopconfirmButton, HorizontalLayout,
	},
	props: {
		loading: {},
		status: {},
		settings: {},
		fetchSettings: {},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	setup(props) {
		const settingsRef = toRef(props, 'settings');
		const maxCssrMountpointsCount = ref(1);
		const filterSelectedId = ref(null);
		const filterOptions = computed(() => {
			if (settingsRef.value != null && settingsRef.value.cssrMountpoints != null) {
				return settingsRef.value.cssrMountpoints.map(item => ({
					value: item.id,
					label: settingsRef.value.finalNameCssr + '_' + item.label,
				}));
			}
			return [];
		});
		watch(filterOptions, items => {
			if (items.length > 0 && !items.some(item => item.value === filterSelectedId.value)) {
				filterSelectedId.value = items[0].value;
			}
		});
		const { smallScreen } = useSmallScreen(780);
		const buttonsDirection = computed(() => smallScreen.value ? 'vertical' : 'horizontal');

		const onCreateCssrMountpointClick = () => {};
		const onDeleteCssrMountpointConfirm = () => {};

		const cssrFilters = computed(() => {
			if (settingsRef.value != null && settingsRef.value.cssrMountpoints != null) {
				const selectedCssrFilters = settingsRef.value.cssrMountpoints.find(item => item.id === filterSelectedId.value);
				if (selectedCssrFilters != null) {
					return selectedCssrFilters;
				}
			}
			return {
				id: 0,
				cssr_messages_filters: [],
				mmi_mode: 0,
			};
		});

		const showTopButtons = ref(false);

		const isMmiDescExpanded = ref(false);
		const toggleMmiDesc = () => {
			isMmiDescExpanded.value = !isMmiDescExpanded.value;
		};

		const mmiModeEnabled = ref(false);
		watch(cssrFilters, value => {
			mmiModeEnabled.value = Number(value?.mmi_mode) === 1;
		}, { immediate: true });

		const isMmiModeSubmitting = ref(false);
		const onMmiModeChange = value => {
			isMmiModeSubmitting.value = true;
			Api.cssrMountpointVrsUpdateMmiMode(cssrFilters.value.id, value ? 1 : 0, response => {
				isMmiModeSubmitting.value = false;
				if (response.status === 'ok') {
					props.fetchSettings(() => {}, true); // hide loading
				} else {
					props.fetchSettings();
					showErrorMessage(response.errors);
				}
			});
		};

		return {
			showTopButtons,
			filterSelectedId,
			filterOptions,
			cssrFilters,
			isMmiDescExpanded,
			toggleMmiDesc,
			mmiModeEnabled,
			isMmiModeSubmitting,
			onMmiModeChange,
			buttonsDirection,
			maxCssrMountpointsCount,
			onCreateCssrMountpointClick,
			onDeleteCssrMountpointConfirm,
		}
	},
}
</script>

<style scoped>
.ui-select {
	width: clamp(100px, 30vw, 400px);
}
.mmi-mode-container {
	display: flex;
	gap: 16px;
	align-items: center;
}
.mmi-hint-icon {
	font-size: 14px;
	color: rgba(0, 0, 0, 0.45);
	cursor: pointer;
	vertical-align: middle;
	transition: color 0.2s ease;
}
.mmi-hint-icon--active {
	color: rgba(0, 0, 0, 0.88);
}
.mmi-description-collapse {
	display: grid;
	grid-template-rows: 0fr;
	transition: grid-template-rows 0.2s ease;
}
.mmi-description-collapse--expanded {
	grid-template-rows: 1fr;
}
.mmi-description-collapse__inner {
	overflow: hidden;
}
.mmi-description-text {
	margin-bottom: 0;
	color: rgba(0, 0, 0, 0.88);
}
.mmi-description-text + .mmi-description-text {
	margin-top: 8px;
}
@media (max-width: 900px) {
	.ui-select {
		width: 100%;
	}
	.mmi-mode-container {
		flex-wrap: wrap;
	}
}
</style>
